import { useState, useEffect, useCallback } from 'react'
import { SelectMenuProps } from './select-menu.types'

const LOADING_SIMPLE_ITEMS_COUNT = 2
const LOADING_ITEMS_WITH_ICON_COUNT = 2

export const SIMPLE_ITEM_HEIGHT = 44
export const ITEM_WITH_ICON_HEIGHT = 60
const DIVIDER_HEIGHT = 1

const getSelectMenuHeight = (optionsCount: number, height: number) =>
  optionsCount * height + (optionsCount - 1) * DIVIDER_HEIGHT

export function useSelectMenu({
  headerTexts = [],
  getHeaderText = (depth: number, selectedOptions: string[]) =>
    selectedOptions[depth - 1],
  optionFetchers,
  optionStatusFetchers,
  iconFetchers,
  maxHeight,
  onDone,
}: Omit<SelectMenuProps, 'onExit'>) {
  const LOADING_ITEMS_COUNT = iconFetchers
    ? LOADING_ITEMS_WITH_ICON_COUNT
    : LOADING_SIMPLE_ITEMS_COUNT

  const ITEMS_HEIGHT = iconFetchers ? ITEM_WITH_ICON_HEIGHT : SIMPLE_ITEM_HEIGHT

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [options, setOptions] = useState<string[]>(
    Array(LOADING_ITEMS_COUNT).fill(''),
  )
  const [icons, setIcons] = useState<JSX.Element[]>(
    Array(LOADING_ITEMS_COUNT).fill(null),
  )

  const [optionsStatus, setOptionsStatus] = useState<
    {
      disabled?: boolean
      badge: JSX.Element
    }[]
  >(Array(LOADING_ITEMS_COUNT).fill({ disabled: false, badge: null }))

  const [loading, setLoading] = useState<boolean>(true)
  const [depth, setDepth] = useState<number>(0)

  const totalDepth = optionFetchers.length

  useEffect(() => {
    setLoading(true)
    optionFetchers[depth](selectedOptions)
      .then(setOptions)
      .then(() => iconFetchers?.[depth](selectedOptions).then(setIcons))
      .then(() =>
        optionStatusFetchers?.[depth](selectedOptions).then(setOptionsStatus),
      )
      .finally(() => setLoading(false))
  }, [depth])

  const onOptionClick = useCallback(
    (value: string) => {
      const updatedSelectedOptions = [...selectedOptions, value]
      setSelectedOptions(updatedSelectedOptions)

      if (depth + 1 >= optionFetchers.length) {
        onDone(updatedSelectedOptions)
        return
      }

      setDepth(depth + 1)
    },
    [depth, optionFetchers],
  )

  const onBackClick = useCallback(() => {
    setSelectedOptions(selectedOptions.slice(0, -1))
    setDepth(depth - 1)
  }, [depth, optionFetchers])

  const headerText = headerTexts?.length
    ? headerTexts[depth]
    : getHeaderText(depth, selectedOptions)

  return {
    isFirstStep: depth === 0,
    isLastDepth: depth === totalDepth - 1,
    options,
    icons,
    loading,
    headerText,
    selectMenuHeight: Math.min(
      getSelectMenuHeight(options.length || LOADING_ITEMS_COUNT, ITEMS_HEIGHT),
      maxHeight,
    ),
    onOptionClick,
    onBackClick,
    optionsStatus,
  }
}
